import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../auth.service';
import { normalizeVaultMenuRouteKey, VAULT_MENU_CANONICAL } from './vault-menu-canonical.snapshot';
import {
  getVaultSidebarAllowedRouteKeys,
  isVaultSidebarRouteAllowed,
  resetVaultSidebarPermissionsPending,
  setVaultSidebarAllowedRouteKeys,
  shouldBlockVaultRouteAccess,
} from './vault-sidebar-menu-merge.util';
import {
  resolveVaultQaRoleProfileForRestrictions,
  userIsAdminEmpresaFromSession,
  userIsSuperadminFromSession,
} from './vault-role-menu-matrix.util';
import type { VaultSidebarMenuItem } from './vault-sidebar-menu.types';

export interface VaultMenuAccessDeniedEvent {
  message: string;
  moduleLabel?: string;
  route?: string;
}

@Injectable({ providedIn: 'root' })
export class VaultMenuAccessService {
  private readonly auth = inject(AuthService);
  private readonly deniedSubject = new BehaviorSubject<VaultMenuAccessDeniedEvent | null>(null);
  readonly accessDenied$ = this.deniedSubject.asObservable();

  setAllowedRouteKeys(keys: Set<string>): void {
    setVaultSidebarAllowedRouteKeys(keys);
  }

  resetPermissionsPending(): void {
    resetVaultSidebarPermissionsPending();
  }

  shouldBlockAccess(route: string): boolean {
    return shouldBlockVaultRouteAccess(route);
  }

  getAllowedRouteKeys(): Set<string> {
    return getVaultSidebarAllowedRouteKeys();
  }

  isRouteAllowed(route: string): boolean {
    return isVaultSidebarRouteAllowed(route);
  }

  /** Mismo texto que la tarjeta in-module: "No tienes permisos para ver …". */
  notifyModuleViewAccessDenied(moduleLabel: string, route?: string): void {
    if (!this.shouldEmitAccessDeniedToast(route)) return;
    const message = this.buildModuleViewDeniedMessage(moduleLabel);
    this.deniedSubject.next({ message, moduleLabel, route });
  }

  /** Clic en menú / pestaña sin permiso. */
  notifyAccessDenied(moduleLabel: string, route?: string): void {
    this.notifyModuleViewAccessDenied(moduleLabel, route);
  }

  /** Toast en el área principal (p. ej. clic en menú sin permiso). */
  notifyVaultToast(message: string, title = 'Acceso restringido'): void {
    if (!this.shouldEmitAccessDeniedToast()) return;
    this.deniedSubject.next({ message, moduleLabel: title });
  }

  /** Superadmin / Admin empresa: no toast de permisos si el módulo abre (#0000149). */
  private shouldSkipAccessDeniedToastForSession(): boolean {
    if (userIsSuperadminFromSession() || this.auth.isVaultSuperadmin()) {
      return true;
    }
    if (userIsAdminEmpresaFromSession() || this.auth.isVaultAdminEmpresa()) {
      return true;
    }
    return resolveVaultQaRoleProfileForRestrictions() === 'admin';
  }

  private shouldEmitAccessDeniedToast(route?: string): boolean {
    if (this.shouldSkipAccessDeniedToastForSession()) return false;
    const normalizedRoute = route?.trim();
    if (normalizedRoute) {
      return this.shouldBlockAccess(normalizedRoute);
    }
    return true;
  }

  clearAccessDenied(): void {
    this.deniedSubject.next(null);
  }

  getLabelForRoute(url: string): string {
    const key = normalizeVaultMenuRouteKey(url);
    const found = this.findLabelInMenu(VAULT_MENU_CANONICAL, key);
    return found ?? 'este módulo';
  }

  buildModuleViewDeniedMessage(moduleLabel: string): string {
    return `No tienes permisos para ver ${moduleLabel}.`;
  }

  buildAccessDeniedMessage(moduleLabel: string): string {
    return this.buildModuleViewDeniedMessage(moduleLabel);
  }

  private findLabelInMenu(items: VaultSidebarMenuItem[], routeKey: string): string | null {
    for (const item of items) {
      if (item.children?.length) {
        const child = this.findLabelInMenu(item.children, routeKey);
        if (child) return child;
      } else if (item.route && normalizeVaultMenuRouteKey(item.route) === routeKey) {
        return item.label?.trim() || null;
      }
    }
    return null;
  }
}
